import type { ReactNode } from 'react';
import { createContext, useContext, useState, useCallback } from 'react';
import { Toast } from './Toast';
import type { ToastVariant } from './Toast';
import styles from './Toast.module.css';

export interface ToastItem {
  id: string;
  variant: ToastVariant;
  message: string;
  duration?: number;
  autoClose?: boolean;
}

interface ToastContextValue {
  toasts: ToastItem[];
  showToast: (variant: ToastVariant, message: string, options?: { duration?: number; autoClose?: boolean }) => string;
  removeToast: (id: string) => void;
  success: (message: string, options?: { duration?: number; autoClose?: boolean }) => string;
  error: (message: string, options?: { duration?: number; autoClose?: boolean }) => string;
  warning: (message: string, options?: { duration?: number; autoClose?: boolean }) => string;
  info: (message: string, options?: { duration?: number; autoClose?: boolean }) => string;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export interface ToastProviderProps {
  children: ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  maxToasts?: number;
}

export function ToastProvider({
  children,
  position = 'top-right',
  maxToasts = 5,
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (
      variant: ToastVariant,
      message: string,
      options?: { duration?: number; autoClose?: boolean }
    ): string => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newToast: ToastItem = {
        id,
        variant,
        message,
        duration: options?.duration ?? 8000,
        autoClose: options?.autoClose ?? true,
      };

      setToasts((prev) => {
        const updated = [...prev, newToast];
        // Limit the number of toasts
        return updated.slice(-maxToasts);
      });

      return id;
    },
    [maxToasts]
  );

  const success = useCallback(
    (message: string, options?: { duration?: number; autoClose?: boolean }) =>
      showToast('success', message, options),
    [showToast]
  );

  const error = useCallback(
    (message: string, options?: { duration?: number; autoClose?: boolean }) =>
      showToast('error', message, options),
    [showToast]
  );

  const warning = useCallback(
    (message: string, options?: { duration?: number; autoClose?: boolean }) =>
      showToast('warning', message, options),
    [showToast]
  );

  const info = useCallback(
    (message: string, options?: { duration?: number; autoClose?: boolean }) =>
      showToast('info', message, options),
    [showToast]
  );

  const value: ToastContextValue = {
    toasts,
    showToast,
    removeToast,
    success,
    error,
    warning,
    info,
  };

  const containerClassNames = [
    styles.container,
    styles[`position-${position}`],
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.length > 0 && (
        <div className={containerClassNames} role="region" aria-label="Notificaciones">
          {toasts.map((toast, index) => (
            <Toast
              key={toast.id}
              variant={toast.variant}
              message={toast.message}
              duration={toast.duration}
              autoClose={toast.autoClose}
              onClose={() => removeToast(toast.id)}
              style={{
                marginBottom: index < toasts.length - 1 ? 'var(--spacing-2)' : 0,
              }}
            />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToastContext(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
}

